import { useEffect } from 'react';
import type { ArticleV1 as ArticleV1Type } from 'types/capi/article';

import ArticleFooter from '~/components/article/ArticleFooter';
import CommissionDisclaimer from '~/components/article/CommissionDisclaimer';
import { ArticleByline } from '~/components/article/HeaderFooter';
import Content, { Constrainable } from '~/components/Content';
import Image from '~/components/Image';
import ViewNewsArticleEvent from '~/events/ViewNewsArticleEvent';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';

import Badge from './Badge';
import Tags from './Tags';

export default function ArticleV1() {
  const article = usePageData<ArticleV1Type>();
  const { clientIp, isBot } = useRootData();
  const user = useUser();

  useEffect(() => {
    new ViewNewsArticleEvent({
      article,
      clientIp,
      isBot,
      user
    }).send();
  }, [article, user, clientIp, isBot]);

  useStructuredData(article._schemas);

  return (
    <>
      <CommissionDisclaimer />
      <div className="bg-white">
        <div className="content-width flex flex-col items-center justify-center gap-6 py-6 md:pb-10">
          <div className="relative flex flex-col items-center lg:mb-4">
            <Image
              caption={article.featured_image.caption}
              src={article.featured_image.url}
              alt={article.featured_image.alt}
              loading="eager"
              responsiveOptions={{
                lg: { height: 672, width: 1160 },
                md: { height: 450, width: 1024 },
                sm: { height: 450, width: 800 },
                xs: { height: 300, width: 672 }
              }}
            />
            <Badge badge={article.badge} />
          </div>

          <Constrainable
            className="flex flex-col items-center gap-4"
            constrain={false}
          >
            <h1 className="text-hero w-full text-balance text-center">
              {article.title}
            </h1>
            {article.author && (
              <ArticleByline
                author={article.author}
                publishedDate={article.date.published}
              />
            )}
            <Tags tags={article.tags} />
          </Constrainable>
        </div>
      </div>

      <div className="bg-grey-tile py-6 md:py-16">
        <div className="content-width">
          <Content
            constrainTextWidth={true}
            content={article.content}
            wideSpacing
          />
        </div>
      </div>

      <ArticleFooter
        articleType="news"
        latestNews={article.latest_news}
        recentFeatures={article.recent_features}
      />
    </>
  );
}
